import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Detail } from "./Detail";

const PURPLE = "#6B3FA0";
const YELLOW = "#FCC83D";
const INK = "#0D1B2A";
const GREY = "#595959";
const MONO = "JetBrains Mono, monospace";

const ITEMS = {
  dye: { n: "Indicator dye", title: "A pH-sensitive dye", text: "The dye sits in the indicator layer, behind the window. Its colour depends only on the pH around it: neutral, with fresh meat, it reads purple; as the layer turns basic it reads yellow. The dye is never consumed and nothing is added to it — the same molecule simply changes protonation state." },
  gases: { n: "Spoilage volatiles", title: "Ammonia (NH₃), trimethylamine, other amines", text: "Bacteria break down the proteins in meat and release volatile basic compounds. They accumulate in the sealed headspace and travel upwards into the label — the only thing that crosses into the indicator layer is gas." },
  ph: { n: "The mechanism", title: "The pH shift, not the particle", text: "In the moisture held by the indicator matrix the gas reacts: NH₃ + H₂O ⇌ NH₄⁺ + OH⁻. The hydroxide raises the local pH, and it is that pH change the dye reports. The colour is not the gas arriving or a pigment moving — it is the dye responding to its new chemical environment." },
  note: { n: "Note", title: "Why gases, not ammonium", text: "Ammonium (NH₄⁺) is the product of the reaction in water, not the travelling species — it is not a gas under normal conditions. What moves from the food to the label is volatile: NH₃ and other amines." },
};

const PH_LABELS = ["6", "7", "8", "9"];

export const ChemistryDiagram = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  const [sel, setSel] = useState("ph");
  const [narrow, setNarrow] = useState(false);
  const d = ITEMS[sel];

  useEffect(() => {
    setNarrow(window.matchMedia("(max-width: 767px)").matches);
  }, []);

  const marker = (x1, x2, y, r) => (
    <motion.circle
      cy={y}
      r={r}
      fill={INK}
      initial={{ cx: x1 }}
      animate={inView ? { cx: x2 } : { cx: x1 }}
      transition={{ duration: 3.2, ease: [0.4, 0, 0.2, 1], delay: 0.4 }}
      data-testid="chem-ph-marker"
    />
  );

  return (
    <div className="grid grid-cols-12 gap-x-6 gap-y-8" data-testid="chemistry-diagram">
      <div className="col-span-12 lg:col-span-7" ref={ref}>
        {narrow ? (
          <svg viewBox="0 0 360 600" className="w-full h-auto">
            <g onClick={() => setSel("dye")} data-testid="chem-fresh">
              <rect x="0" y="0" width="360" height="170" fill="transparent" />
              <circle cx="180" cy="62" r="50" fill={PURPLE} stroke={INK} />
              <text x="180" y="134" textAnchor="middle" fontFamily={MONO} fontSize="13" letterSpacing="1.5" fill={INK}>PURPLE</text>
              <text x="180" y="154" textAnchor="middle" fontFamily={MONO} fontSize="11" letterSpacing="1" fill={GREY}>FRESH · pH ≈ 6</text>
            </g>
            <path d="M180 172 L 180 208 M174 200 L 180 208 L 186 200" fill="none" stroke={INK} strokeOpacity="0.5" />
            <g onClick={() => setSel("dye")} data-testid="chem-spoiled">
              <rect x="0" y="206" width="360" height="170" fill="transparent" />
              <circle cx="180" cy="266" r="50" fill={YELLOW} stroke={INK} data-testid="chem-result-dot" />
              <text x="180" y="338" textAnchor="middle" fontFamily={MONO} fontSize="13" letterSpacing="1.5" fill={INK}>YELLOW</text>
              <text x="180" y="358" textAnchor="middle" fontFamily={MONO} fontSize="11" letterSpacing="1" fill={GREY}>SPOILED · pH ≈ 8.5</text>
            </g>
            <line x1="20" y1="382" x2="340" y2="382" stroke={INK} strokeOpacity="0.15" />
            <g onClick={() => setSel("ph")} data-testid="chem-ph">
              <rect x="0" y="384" width="360" height="158" fill="transparent" />
              <text x="180" y="412" textAnchor="middle" fontFamily={MONO} fontSize="13" letterSpacing="0.5" fill={INK}>NH₃ + H₂O ⇌ NH₄⁺ + OH⁻</text>
              <text x="180" y="432" textAnchor="middle" fontFamily={MONO} fontSize="10" letterSpacing="0.5" fill={GREY}>DISSOLVES IN THE MOIST MATRIX</text>
              <line x1="40" y1="466" x2="320" y2="466" stroke={INK} />
              {PH_LABELS.map((lb, i) => {
                const x = 40 + i * 93.3;
                return (
                  <g key={lb}>
                    <line x1={x} y1="460" x2={x} y2="472" stroke={INK} strokeOpacity="0.5" />
                    <text x={x} y="490" textAnchor="middle" fontFamily={MONO} fontSize="10" fill={GREY}>{lb}</text>
                  </g>
                );
              })}
              {marker(40, 320, 466, 7)}
              <text x="180" y="518" textAnchor="middle" fontFamily={MONO} fontSize="12" letterSpacing="1" fill={INK}>LOCAL pH RISES →</text>
              <text x="180" y="536" textAnchor="middle" fontFamily={MONO} fontSize="10" letterSpacing="0.5" fill={GREY}>THE DYE READS THE pH, NOT THE GAS</text>
            </g>
            <g onClick={() => setSel("gases")} data-testid="chem-gases">
              <rect x="0" y="544" width="360" height="56" fill="transparent" />
              {[100, 180, 260].map((x, i) => (
                <motion.circle
                  key={x}
                  cx={x}
                  r="4"
                  fill={INK}
                  initial={{ cy: 566, opacity: 0 }}
                  animate={{ cy: [566, 548], opacity: [0, 1, 0] }}
                  transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.6, ease: "easeOut" }}
                />
              ))}
              <rect x="20" y="570" width="320" height="26" fill="#B5483F" stroke={INK} />
              <text x="180" y="587" textAnchor="middle" fontFamily={MONO} fontSize="10" letterSpacing="1" fill="#F2F3F5">MEAT · NH₃ RISES INTO THE LABEL</text>
            </g>
          </svg>
        ) : (
          <svg viewBox="0 0 640 300" className="w-full h-auto">
            <g onClick={() => setSel("dye")} style={{ cursor: "pointer" }} data-testid="chem-fresh">
              <rect x="20" y="30" width="150" height="200" fill="transparent" />
              <circle cx="92" cy="104" r="62" fill={PURPLE} stroke={INK} />
              <text x="92" y="196" textAnchor="middle" fontFamily={MONO} fontSize="11" letterSpacing="1.5" fill={INK}>PURPLE</text>
              <text x="92" y="214" textAnchor="middle" fontFamily={MONO} fontSize="9" letterSpacing="1" fill={GREY}>FRESH · pH ≈ 6</text>
            </g>

            <g onClick={() => setSel("dye")} style={{ cursor: "pointer" }} data-testid="chem-spoiled">
              <rect x="470" y="30" width="150" height="200" fill="transparent" />
              <circle cx="548" cy="104" r="62" fill={YELLOW} stroke={INK} data-testid="chem-result-dot" />
              <text x="548" y="196" textAnchor="middle" fontFamily={MONO} fontSize="11" letterSpacing="1.5" fill={INK}>YELLOW</text>
              <text x="548" y="214" textAnchor="middle" fontFamily={MONO} fontSize="9" letterSpacing="1" fill={GREY}>SPOILED · pH ≈ 8.5</text>
            </g>

            <g onClick={() => setSel("ph")} style={{ cursor: "pointer" }} data-testid="chem-ph">
              <rect x="176" y="30" width="288" height="160" fill="transparent" />
              <text x="320" y="48" textAnchor="middle" fontFamily={MONO} fontSize="12" letterSpacing="1" fill={INK}>NH₃ + H₂O ⇌ NH₄⁺ + OH⁻</text>
              <text x="320" y="68" textAnchor="middle" fontFamily={MONO} fontSize="9" letterSpacing="1" fill={GREY}>DISSOLVES IN THE MOISTURE OF THE MATRIX</text>

              <line x1="156" y1="110" x2="190" y2="110" stroke={INK} strokeOpacity="0.25" />
              <line x1="450" y1="110" x2="484" y2="110" stroke={INK} strokeOpacity="0.25" />
              <line x1="190" y1="110" x2="450" y2="110" stroke={INK} />
              {PH_LABELS.map((lb, i) => {
                const x = 190 + i * 86.7;
                return (
                  <g key={lb}>
                    <line x1={x} y1="104" x2={x} y2="116" stroke={INK} strokeOpacity="0.5" />
                    <text x={x} y="132" textAnchor="middle" fontFamily={MONO} fontSize="9" fill={GREY}>{lb}</text>
                  </g>
                );
              })}
              {marker(190, 450, 110, 6)}
              <text x="320" y="158" textAnchor="middle" fontFamily={MONO} fontSize="11" letterSpacing="1.5" fill={INK}>LOCAL pH RISES →</text>
              <text x="320" y="178" textAnchor="middle" fontFamily={MONO} fontSize="9" letterSpacing="1" fill={GREY}>THE DYE READS THE pH, NOT THE GAS</text>
            </g>

            <g onClick={() => setSel("gases")} style={{ cursor: "pointer" }} data-testid="chem-gases">
              <rect x="176" y="190" width="288" height="100" fill="transparent" />
              {[214, 320, 426].map((x, i) => (
                <motion.circle
                  key={x}
                  cx={x}
                  r="3.5"
                  fill={INK}
                  initial={{ cy: 252, opacity: 0 }}
                  animate={{ cy: [252, 214], opacity: [0, 1, 0] }}
                  transition={{ duration: 2.6, repeat: Infinity, delay: i * 0.6, ease: "easeOut" }}
                />
              ))}
              <rect x="176" y="256" width="288" height="26" fill="#B5483F" stroke={INK} />
              <text x="320" y="273" textAnchor="middle" fontFamily={MONO} fontSize="9" letterSpacing="1.5" fill="#F2F3F5">MEAT · SPOILAGE BACTERIA</text>
              <text x="176" y="246" fontFamily={MONO} fontSize="9" letterSpacing="1" fill={GREY}>NH₃ / TMA ↑</text>
            </g>
          </svg>
        )}
        <button type="button" onClick={() => setSel("note")} className="mono text-[0.65rem] tracking-[0.15em] uppercase text-[#595959] underline underline-offset-4 mt-2 py-2 inline-block" data-testid="chem-note-button">
          Note on ammonium
        </button>
      </div>
      <div className="col-span-12 lg:col-span-5">
        <Detail n={d.n} title={d.title} text={d.text} testId="chemistry-detail" />
      </div>
    </div>
  );
};
